import React, { useId, useMemo } from "react";
import { Box } from "@mui/material";
import { DASHBOARD } from "./dashboardTheme";

export type PenaltyTrendYearPoint = {
  year: number;
  amount: number;
};

const CHART_HEIGHT = 220;
const MARGIN = { top: 12, right: 16, bottom: 32, left: 56 };

const GRID_COLOR = "#E8EEF0";

function formatAxisUsd(amount: number): string {
  if (amount >= 1_000_000) {
    const m = amount / 1_000_000;
    return `$${m % 1 === 0 ? m.toFixed(0) : m.toFixed(1)}M`;
  }
  if (amount >= 1_000) {
    return `$${Math.round(amount / 1_000)}k`;
  }
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(amount);
}

/** Round Y-axis max and step for readable grid lines. */
export function computePenaltyTrendYScale(maxAmount: number): { yMax: number; step: number } {
  if (maxAmount <= 0) {
    return { yMax: 100_000, step: 20_000 };
  }
  const targetTicks = 6;
  const roughStep = maxAmount / (targetTicks - 1);
  const magnitude = 10 ** Math.floor(Math.log10(roughStep));
  const normalized = roughStep / magnitude;
  let step: number;
  if (normalized <= 1) step = magnitude;
  else if (normalized <= 2) step = 2 * magnitude;
  else if (normalized <= 5) step = 5 * magnitude;
  else step = 10 * magnitude;
  const yMax = Math.ceil(maxAmount / step) * step;
  return { yMax, step };
}

export function penaltyTrendYTicks(yMax: number, step: number): number[] {
  const ticks: number[] = [];
  for (let v = 0; v <= yMax + step * 0.001; v += step) {
    ticks.push(v);
  }
  return ticks;
}

export default function PenaltyTrendLineChart({
  points,
}: {
  points: PenaltyTrendYearPoint[];
}) {
  const gradientId = useId().replace(/:/g, "");

  const sorted = useMemo(
    () => [...points].sort((a, b) => a.year - b.year),
    [points],
  );

  const { yMax, step, ticks, coords, innerW, innerH } = useMemo(() => {
    const maxAmount = Math.max(...sorted.map((p) => p.amount), 0);
    const { yMax: scaleMax, step: scaleStep } = computePenaltyTrendYScale(maxAmount);
    const yTicks = penaltyTrendYTicks(scaleMax, scaleStep);
    const innerW = 560;
    const innerH = CHART_HEIGHT - MARGIN.top - MARGIN.bottom;
    const n = sorted.length;
    const xAt = (i: number) => (n > 1 ? (i / (n - 1)) * innerW : innerW / 2);
    const yAt = (amount: number) =>
      innerH - (scaleMax > 0 ? (amount / scaleMax) * innerH : 0);

    const coords = sorted.map((p, i) => ({
      year: p.year,
      amount: p.amount,
      x: xAt(i),
      y: yAt(p.amount),
    }));

    return {
      yMax: scaleMax,
      step: scaleStep,
      ticks: yTicks,
      coords,
      innerW,
      innerH,
    };
  }, [sorted]);

  if (!sorted.length) {
    return null;
  }

  const width = MARGIN.left + innerW + MARGIN.right;
  const height = CHART_HEIGHT;
  const linePoints = coords.map((c) => `${c.x},${c.y}`).join(" ");

  return (
    <Box sx={{ width: "100%", overflow: "hidden" }}>
      <Box
        component="svg"
        width="100%"
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label={`Penalty trend by year: ${sorted
          .map((p) => `${p.year} ${formatAxisUsd(p.amount)}`)
          .join(", ")}`}
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={DASHBOARD.metricAccent} stopOpacity={0.12} />
            <stop offset="100%" stopColor={DASHBOARD.metricAccent} stopOpacity={0} />
          </linearGradient>
        </defs>

        <g transform={`translate(${MARGIN.left}, ${MARGIN.top})`}>
          {ticks.map((tick) => {
            const y = innerH - (yMax > 0 ? (tick / yMax) * innerH : 0);
            return (
              <g key={tick}>
                <line
                  x1={0}
                  y1={y}
                  x2={innerW}
                  y2={y}
                  stroke={GRID_COLOR}
                  strokeWidth={1}
                />
                <text
                  x={-10}
                  y={y + 4}
                  textAnchor="end"
                  fill={DASHBOARD.text}
                  fontSize={11}
                  fontFamily="Rethink Sans, sans-serif"
                >
                  {formatAxisUsd(tick)}
                </text>
              </g>
            );
          })}

          <line x1={0} y1={innerH} x2={innerW} y2={innerH} stroke="#C5D5D8" strokeWidth={1} />

          {coords.length > 1 && (
            <polygon
              points={`0,${innerH} ${linePoints} ${innerW},${innerH}`}
              fill={`url(#${gradientId})`}
            />
          )}

          {coords.length > 1 && (
            <polyline
              fill="none"
              stroke={DASHBOARD.metricAccent}
              strokeWidth={2.5}
              strokeLinejoin="round"
              strokeLinecap="round"
              points={linePoints}
            />
          )}

          {coords.map((c) => (
            <g key={c.year}>
              <circle
                cx={c.x}
                cy={c.y}
                r={5}
                fill="#ffffff"
                stroke={DASHBOARD.metricAccent}
                strokeWidth={2}
              />
              <text
                x={c.x}
                y={innerH + 22}
                textAnchor="middle"
                fill={DASHBOARD.header}
                fontSize={12}
                fontWeight={600}
                fontFamily="Rethink Sans, sans-serif"
              >
                {c.year}
              </text>
            </g>
          ))}
        </g>
      </Box>
    </Box>
  );
}
